import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button
} from '@mui/material';

import {
    Bolt as BoltIcon,
    Opacity as OpacityIcon,
    Whatshot as WhatshotIcon,
    Group as GroupIcon,
    CheckCircle as CheckCircleIcon,
    Savings as SavingsIcon,
    EmojiObjects as EmojiObjectsIcon,
    AssignmentTurnedIn as AssignmentTurnedInIcon,
    LocalLaundryService as LocalLaundryServiceIcon,
    Kitchen as KitchenIcon,
    Nature as NatureIcon
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { label: '導入', sectionId: 'introduction' },
    { label: '電気代を抑えるテクニック', sectionId: 'electricity' },
    { label: 'ガス代を節約するコツ', sectionId: 'gas' },
    { label: '水道代節約アイデア', sectionId: 'water' },
    { label: '共同生活で光熱費を抑えるコツ', sectionId: 'sharedliving' },
    { label: 'おすすめ節約グッズ3選', sectionId: 'goods' },
    { label: '光熱費の分担方法アイデア', sectionId: 'sharing' },
    { label: 'まとめ', sectionId: 'conclusion' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.sectionId} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#888',
                                        fontWeight: 'bold',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1,
                                        fontSize: '1.1rem'
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.sectionId)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.97rem', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアで「光熱費」を賢く節約！そのコツとアイデアを一挙紹介
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは家賃だけでなく、光熱費もみんなで分け合うことで一人暮らしよりお得になる大きなメリットがあります。でも「思ったほど安くならない」「使いすぎてトラブルに…」そんな声もよく聞きます。<br />
                    本記事では、<b>電気・ガス・水道の具体的な節約テクニック</b>はもちろん、<b>共同生活ならではの工夫</b>や<b>みんなが納得できる分担方法</b>まで、実用的なアイデアをまとめてご紹介。<br />
                    光熱費を抑えつつ、気持ちよく暮らすためのヒントが満載です！
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>

                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, bgcolor: '#f9fbe7', borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                この記事でわかること
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <BoltIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText primary="電気・ガス・水道別の節約テクニック" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <GroupIcon color="success" />
                                    </ListItemIcon>
                                    <ListItemText primary="ルームメイトと気持ちよくやりくりするコツ" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <EmojiObjectsIcon color="warning" />
                                    </ListItemIcon>
                                    <ListItemText primary="おすすめの節約グッズ・分担方法" />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* 電気代 */}
            <Box id="electricity" sx={{ mb: 5 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h4" component="h2" gutterBottom>
                    <BoltIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                    電気代を抑えるテクニック
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアでは複数人で家電を使う分、油断すると電気代がかさみがち。基本の見直しから一歩進んだ工夫まで、今日からできる節約術をまとめました。
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="冷蔵庫の開閉回数を減らす"
                            secondary="飲み物や調味料はまとめて取り出し、開けっ放しを避けることで消費電力をカット。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <NatureIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="LED照明に交換"
                            secondary="共用部の電球をLEDにするだけで、長期的に大きな節約効果が期待できます。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <LocalLaundryServiceIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="洗濯はまとめて"
                            secondary="全員でスケジュールを合わせ、洗濯機の回数を減らしましょう。乾燥機の使用も控えめに。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <KitchenIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText
                            primary="キッチン家電の待機電力カット"
                            secondary="電子レンジや炊飯器は使わないときコンセントを抜くのが効果的。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <SavingsIcon color="action" />
                        </ListItemIcon>
                        <ListItemText
                            primary="エアコンは温度設定とサーキュレーター活用"
                            secondary="夏は28℃・冬は20℃を目安にし、サーキュレーターで効率アップ。"
                        />
                    </ListItem>
                </List>
                <Box sx={{ mt: 2 }}>
                    <img
                        src="/images/blog/energy-saving-living_roomshare-kounetsuhi-setsuyaku-matome.jpg"
                        alt="昼間の明るいリビングで、ルームメイトたちがLED照明とサーキュレーターを活用しながら快適に過ごしている様子。白を基調としたインテリアと節電グッズがポイント"
                        style={{ width: '100%', borderRadius: 8, boxShadow: '0 2px 12px #bbb' }}
                    />
                </Box>
            </Box>

            {/* ガス代 */}
            <Box id="gas" sx={{ mb: 5 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h4" component="h2" gutterBottom>
                    <WhatshotIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'error.main' }} />
                    ガス代を節約するコツ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    特に冬場や料理好き同士のルームシェアではガス代は高くなりがち。協力して無理せず節約できるポイントを押さえましょう。
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="お風呂は時間をまとめて入る"
                            secondary="追い炊きの回数を減らし、シャワーもできるだけ連続して使うのがコツ。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <KitchenIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="作り置き・まとめ料理"
                            secondary="一度に多めに調理し、再加熱や長時間のガス使用を減らしましょう。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <SavingsIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="圧力鍋や保温調理器の活用"
                            secondary="短時間の加熱でもしっかり火が通り、ガス代も時短も両立。"
                        />
                    </ListItem>
                </List>
            </Box>

            {/* 水道代 */}
            <Box id="water" sx={{ mb: 5 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h4" component="h2" gutterBottom>
                    <OpacityIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'info.main' }} />
                    水道代節約アイデア
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアならではの水道代節約術もたくさん！一人ひとりのちょっとした意識が、年間で数千円〜数万円の節約につながります。
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="お風呂・シャワーの時間を短縮"
                            secondary="湯船の湯量を控えめにし、シャワーは必要な分だけ。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <NatureIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="節水シャワーヘッドの導入"
                            secondary="取り付けが簡単で効果が高いので、シェア住まいこそおすすめ。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <KitchenIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText
                            primary="食器洗いはまとめて"
                            secondary="流しっぱなしを避け、まとめ洗い＆節水モードを活用。"
                        />
                    </ListItem>
                </List>
                <Box sx={{ mt: 2 }}>

                </Box>
            </Box>

            {/* 共同生活のコツ */}
            <Box id="sharedliving" sx={{ mb: 5 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h4" component="h2" gutterBottom>
                    <GroupIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'success.main' }} />
                    共同生活で光熱費を抑えるコツ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    節約術を「みんなで続ける」ためには、お互いの工夫や意識のすれ違いを防ぐことが大切です。実際にルームシェア経験者から高評価だったコツをご紹介します。
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <List dense>
                            <ListItem>
                                <ListItemIcon>
                                    <AssignmentTurnedInIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="チェックリストを共有"
                                    secondary="玄関やキッチンなど、よく目につく場所に「節約リマインダー」を貼りましょう。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <GroupIcon color="success" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="月1の「光熱費ミーティング」"
                                    secondary="請求書が来たタイミングで支出を振り返り、みんなで良かった点や反省点をシェア。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <EmojiObjectsIcon color="warning" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="節約ゲーム感覚でチャレンジ"
                                    secondary="「先月より1,000円安くしよう！」など、数字目標を決めて楽しみながら実践。"
                                />
                            </ListItem>
                        </List>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#f3e5f5', borderRadius: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                専門家からのアドバイス
                            </Typography>
                            <Typography variant="body2">
                                ルームシェアでは「相手の節約意識が低い」とイライラすることも。<br />
                                <b>「なぜ節約したいのか」「どう協力できるか」</b>を話し合うことで、自然と協力体制ができていきます。<br />
                                ピリピリせず、あくまでポジティブに続けるのが長続きのコツです！
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* 節約グッズ */}
            <Box id="goods" sx={{ mb: 5 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h4" component="h2" gutterBottom>
                    <EmojiObjectsIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
                    おすすめ節約グッズ3選
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    投資するだけで光熱費をグッと抑えられる便利グッズをご紹介。初期費用は数千円でも、1年で元が取れることも！
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{ maxWidth: 345 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <NatureIcon color="success" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        節水シャワーヘッド
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    最大50%の水道・ガス代節約も。設置も簡単で、シャワーの質感もアップ。
                                </Typography>
                                <Chip label="水道・ガス節約" color="primary" variant="outlined" size="small" />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{ maxWidth: 345 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <SavingsIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        スマートコンセント
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    スマホで遠隔操作＆使用電力も見える化。ムダな待機電力を自動カット。
                                </Typography>
                                <Chip label="電気代節約" color="secondary" variant="outlined" size="small" />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{ maxWidth: 345 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <BoltIcon color="warning" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                        サーキュレーター
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    エアコンと併用で快適＆節電。部屋の空気を循環させて効率アップ。
                                </Typography>
                                <Chip label="冷暖房効率化" color="success" variant="outlined" size="small" />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>

                </Box>
            </Box>

            {/* 分担方法 */}
            <Box id="sharing" sx={{ mb: 5 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h4" component="h2" gutterBottom>
                    <AssignmentTurnedInIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                    光熱費の分担方法アイデア
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    最後に、「どう分けるか問題」。ルームシェアの満足度は「分担方法」にも左右されます。みんなが納得できるアイデアをご紹介。
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="完全均等割り"
                            secondary="最もシンプル。全員同額でストレスフリー。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                            primary="部屋の広さ・設備に応じて按分"
                            secondary="個室・広い部屋の人はやや多く、共有部主体の人は少なめにと柔軟な計算もアリ。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText
                            primary="利用状況に応じて調整"
                            secondary="テレワークや夜勤など、明らかに使う量が多い場合は、話し合いを。アプリで記録するのも◎。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleIcon color="secondary" />
                        </ListItemIcon>
                        <ListItemText
                            primary="月ごとに担当をローテーション"
                            secondary="支払い役を持ち回りにして、面倒な作業を分散。"
                        />
                    </ListItem>
                </List>
                <Paper elevation={0} sx={{ mt: 2, p: 2, bgcolor: '#fffde7', borderRadius: 2 }}>
                    <Typography variant="body2">
                        分担方法は「みんなが納得」することが最優先。状況が変わったら都度話し合い、柔軟にアップデートしましょう。
                    </Typography>
                </Paper>
            </Box>

            {/* まとめ */}
            <Box id="conclusion" sx={{ mb: 7 }}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    光熱費の節約は、日々のちょっとした意識と「みんなで協力する」工夫がカギ。<br />
                    ルームシェアならではの楽しさを活かして、快適で賢い共同生活を！
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    ぜひ、この記事のアイデアを参考に、無理なく・楽しく・賢く光熱費を節約してみてください。
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-kounetsuhi-setsuyaku-matome',
    slug: 'roomshare-kounetsuhi-setsuyaku-matome',
    title: 'ルームシェアでの「光熱費」節約術まとめ',
    excerpt: '電気・ガス・水道の節約法や共同生活のコツ、節約グッズ、分担方法まで、ルームシェアでの光熱費を賢く抑える実践的なアイデアをまとめて紹介します。',
    coverImage: '/images/blog/cover-share-living-energy_roomshare-kounetsuhi-setsuyaku-matome.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。ナチュラルな木製家具と観葉植物が配置され、全体的に温かく和やかな雰囲気を感じさせる',
    publishedAt: '2024-06-13T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '節約', '共同生活'],
    tags: ['光熱費', '電気代', '節約術', '分担方法', 'グッズ紹介'],
    author: {
        name: '水野 里奈',
        avatar: '/images/avatars/rina-mizuno.jpg',
        bio: 'ルームシェア運営歴10年以上。共同生活の暮らしやすさと節約の知恵を発信中。実体験をもとに、ストレスなく快適なシェア生活を提案します。',
        profilePicture: '/images/avatars/rina-mizuno.jpg'
    },
    viewCount: 1530,
    contentComponent: PostContent
};

export default post;